import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Home, Search, UserPlus, X, ImagePlus, Upload, Link, Loader2, Bed, Bath, Square, MapPin } from 'lucide-react';
import locationsData from '../../data/locations.json';
import { PROPERTY_TYPES } from '../../data/propertyTypes';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { SortableImage } from '../../components/SortableImage';
import { fixImageUrl } from '../../lib/imageUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminListings = () => {
  const [listings, setListings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    address: '',
    district: '',
    city: '',
    state: '',
    zip_code: '',
    property_type: '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    images: [],
    status: 'available',
    assigned_to: '',
    custom_fields: {},
    owner_name: '',
    owner_phone: ''
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageInputMode, setImageInputMode] = useState('upload'); // 'upload' or 'url'
  const fileInputRef = useRef(null);
  const [districtSearch, setDistrictSearch] = useState('');
  const [villageSearch, setVillageSearch] = useState('');
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [showVillageDropdown, setShowVillageDropdown] = useState(false);

  // Get filtered districts
  const getFilteredDistricts = () => {
    const search = districtSearch.toLowerCase();
    if (!search) return locationsData.districts;
    return locationsData.districts.filter(d => d.toLowerCase().includes(search));
  };

  // Get filtered villages - search across all districts if no district selected
  const getFilteredVillages = () => {
    const search = villageSearch.toLowerCase();
    
    if (formData.district) {
      // If district is selected, only show villages from that district
      const villages = locationsData.villages[formData.district] || [];
      if (!search) return villages.slice(0, 15);
      return villages.filter(v => v.toLowerCase().includes(search)).slice(0, 15);
    } else {
      // No district selected - search across all villages
      if (!search) return [];
      const results = [];
      Object.entries(locationsData.villages).forEach(([district, villages]) => {
        villages.forEach(village => {
          if (village.toLowerCase().includes(search)) {
            results.push({ name: village, district: district });
          }
        });
      });
      return results.slice(0, 15);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [listingsRes, employeesRes, fieldsRes] = await Promise.all([
        axios.get(`${API}/listings/`),
        axios.get(`${API}/users/employees`),
        axios.get(`${API}/custom-fields/`)
      ]);
      setListings(listingsRes.data);
      setEmployees(employeesRes.data);
      setCustomFields(fieldsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      price: parseFloat(formData.price),
      bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
      bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
      sqft: formData.sqft ? parseInt(formData.sqft) : null,
      assigned_to: formData.assigned_to === "unassigned" ? null : formData.assigned_to || null
    };

    try {
      if (editingListing) {
        await axios.put(`${API}/listings/${editingListing.id}`, data);
        toast.success('Listing updated successfully');
      } else {
        await axios.post(`${API}/listings/`, data);
        toast.success('Listing created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save listing');
    }
  };

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setFormData({
      title: listing.title,
      description: listing.description || '',
      price: listing.price.toString(),
      address: listing.address,
      district: listing.district || '',
      city: listing.city || '',
      state: listing.state || '',
      zip_code: listing.zip_code || '',
      property_type: listing.property_type || listing.custom_fields?.property_type || '',
      bedrooms: listing.bedrooms?.toString() || '',
      bathrooms: listing.bathrooms?.toString() || '',
      sqft: listing.sqft?.toString() || '',
      images: listing.images || [],
      status: listing.status,
      assigned_to: listing.assigned_to || "unassigned",
      custom_fields: listing.custom_fields || {},
      owner_name: listing.owner_name || '',
      owner_phone: listing.owner_phone || ''
    });
    setDistrictSearch(listing.district || '');
    setVillageSearch(listing.city || '');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await axios.delete(`${API}/listings/${id}`);
      toast.success('Listing deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete listing');
    }
  };

  const resetForm = () => {
    setEditingListing(null);
    setFormData({
      title: '',
      description: '',
      price: '',
      address: '',
      district: '',
      city: '',
      state: '',
      zip_code: '',
      property_type: '',
      bedrooms: '',
      bathrooms: '',
      sqft: '',
      images: [],
      status: 'available',
      assigned_to: 'unassigned',
      custom_fields: {},
      owner_name: '',
      owner_phone: ''
    });
    setNewImageUrl('');
    setDistrictSearch('');
    setVillageSearch('');
  };

  const addImage = () => {
    if (newImageUrl && !formData.images.includes(newImageUrl)) {
      setFormData({ ...formData, images: [...formData.images, newImageUrl] });
      setNewImageUrl('');
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        // Validate file type
        if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
          toast.error(`Invalid file type: ${file.name}`);
          continue;
        }

        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`File too large: ${file.name}`);
          continue;
        }

        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API}/uploads/image`, formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        // Use the URL returned by the backend.
        // Backend now returns absolute Cloudflare R2 URLs (https://pub-...r2.dev/...).
        // Only prepend the backend host for legacy relative URLs (like "/api/uploads/files/...").
        let imageUrl = response.data.url;
        if (imageUrl && !imageUrl.startsWith('http')) {
          imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
        }
        uploadedUrls.push(imageUrl);
      }

      if (uploadedUrls.length > 0) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...uploadedUrls]
        }));
        toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploadingImages(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (url) => {
    setFormData({ ...formData, images: formData.images.filter(img => img !== url) });
  };

  // Drag-and-drop sensors for image sorting
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleImageDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFormData((prev) => {
      const oldIndex = prev.images.indexOf(active.id);
      const newIndex = prev.images.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return { ...prev, images: arrayMove(prev.images, oldIndex, newIndex) };
    });
  };

  const updateCustomField = (key, value) => {
    setFormData({
      ...formData,
      custom_fields: { ...formData.custom_fields, [key]: value }
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const filteredListings = listings.filter(listing => {
    const matchesSearch = listing.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         listing.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || listing.status === statusFilter;
    const listingType = listing.property_type || listing.custom_fields?.property_type || '';
    const matchesType = propertyTypeFilter === 'all' || listingType === propertyTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
            Listings Management
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Manage your property listings
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-primary/90" data-testid="add-listing-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Listing
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold">
                {editingListing ? 'Edit Listing' : 'Add New Listing'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Title *
                  </Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="rounded-none border-2"
                    data-testid="listing-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Price *
                  </Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    className="rounded-none border-2"
                    data-testid="listing-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Status
                  </Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="rounded-none border-2" data-testid="listing-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Property Type
                  </Label>
                  <Select value={formData.property_type || 'none'} onValueChange={(value) => setFormData({ ...formData, property_type: value === 'none' ? '' : value })}>
                    <SelectTrigger className="rounded-none border-2" data-testid="listing-property-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {PROPERTY_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Address *
                  </Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="rounded-none border-2"
                    data-testid="listing-address-input"
                  />
                </div>
                {/* District Dropdown */}
                <div className="space-y-2 relative">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    District
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold" />
                    <Input
                      value={districtSearch}
                      onChange={(e) => {
                        setDistrictSearch(e.target.value);
                        setShowDistrictDropdown(true);
                      }}
                      onFocus={() => setShowDistrictDropdown(true)}
                      onBlur={() => setTimeout(() => setShowDistrictDropdown(false), 200)}
                      placeholder="Search district..."
                      className="rounded-none border-2 pl-10"
                      data-testid="listing-district-input"
                    />
                  </div>
                  
                  {showDistrictDropdown && (
                    <div className="absolute top-full left-0 right-0 bg-white border-2 border-t-0 border-border shadow-lg z-50 max-h-48 overflow-y-auto">
                      {getFilteredDistricts().map((district) => (
                        <button
                          key={district}
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, district: district, city: '' });
                            setDistrictSearch(district);
                            setVillageSearch('');
                            setShowDistrictDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-secondary/50 flex items-center gap-2 border-b border-border/50 last:border-b-0 ${formData.district === district ? 'bg-brand-gold/10' : ''}`}
                        >
                          <MapPin className="w-3 h-3 text-brand-gold" />
                          <span className="font-body text-sm">{district}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Village Dropdown */}
                <div className="space-y-2 relative">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Village
                  </Label>
                  <div className="relative">
                    <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={villageSearch}
                      onChange={(e) => {
                        setVillageSearch(e.target.value);
                        setShowVillageDropdown(true);
                      }}
                      onFocus={() => setShowVillageDropdown(true)}
                      onBlur={() => setTimeout(() => setShowVillageDropdown(false), 200)}
                      placeholder="Search village..."
                      className="rounded-none border-2 pl-10"
                      data-testid="listing-village-input"
                    />
                  </div>
                  
                  {showVillageDropdown && villageSearch && (
                    <div className="absolute top-full left-0 right-0 bg-white border-2 border-t-0 border-border shadow-lg z-50 max-h-48 overflow-y-auto">
                      {getFilteredVillages().length === 0 ? (
                        <div className="px-3 py-2 text-sm text-muted-foreground">
                          {villageSearch.length < 2 ? 'Type at least 2 characters...' : 'No villages found'}
                        </div>
                      ) : (
                        getFilteredVillages().map((item, idx) => {
                          const villageName = typeof item === 'string' ? item : item.name;
                          const districtName = typeof item === 'string' ? formData.district : item.district;
                          return (
                            <button
                              key={`${villageName}-${idx}`}
                              type="button"
                              onClick={() => {
                                if (!formData.district && districtName) {
                                  setFormData({ ...formData, city: villageName, district: districtName });
                                  setDistrictSearch(districtName);
                                } else {
                                  setFormData({ ...formData, city: villageName });
                                }
                                setVillageSearch(villageName);
                                setShowVillageDropdown(false);
                              }}
                              className={`w-full px-3 py-2 text-left hover:bg-secondary/50 flex items-center gap-2 border-b border-border/50 last:border-b-0 ${formData.city === villageName ? 'bg-brand-gold/10' : ''}`}
                            >
                              <Home className="w-3 h-3 text-muted-foreground" />
                              <span className="font-body text-sm">{villageName}</span>
                              {!formData.district && districtName && (
                                <span className="font-body text-xs text-muted-foreground ml-auto">
                                  {districtName}
                                </span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Zip Code
                  </Label>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="rounded-none border-2"
                    data-testid="listing-zip-input"
                  />
                </div>

                {/* Property Details */}
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Bedrooms
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    placeholder="e.g., 3"
                    className="rounded-none border-2"
                    data-testid="listing-bedrooms-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Bathrooms
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.5"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    placeholder="e.g., 2.5"
                    className="rounded-none border-2"
                    data-testid="listing-bathrooms-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Size (m²)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.sqft}
                    onChange={(e) => setFormData({ ...formData, sqft: e.target.value })}
                    placeholder="e.g., 250"
                    className="rounded-none border-2"
                    data-testid="listing-sqft-input"
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Description
                  </Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={10}
                    placeholder="Write a detailed description. Line breaks and empty lines will be preserved on the public page."
                    className="rounded-none border-2 font-body"
                    data-testid="listing-description-input"
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Assign To Employee
                  </Label>
                  <Select value={formData.assigned_to} onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}>
                    <SelectTrigger className="rounded-none border-2" data-testid="listing-assignee-select">
                      <SelectValue placeholder="Select employee..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Property Owner Info */}
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Owner Name
                  </Label>
                  <Input
                    value={formData.owner_name}
                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                    placeholder="Property owner name"
                    className="rounded-none border-2"
                    data-testid="listing-owner-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Owner Phone
                  </Label>
                  <Input
                    value={formData.owner_phone}
                    onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                    placeholder="+961 XX XXX XXX"
                    className="rounded-none border-2"
                    data-testid="listing-owner-phone-input"
                  />
                </div>

                {/* Images */}
                <div className="md:col-span-2 space-y-3">
                  <Label className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                    Images
                  </Label>
                  
                  {/* Toggle between upload and URL */}
                  <div className="flex gap-2 mb-2">
                    <Button
                      type="button"
                      variant={imageInputMode === 'upload' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setImageInputMode('upload')}
                      className={`rounded-none ${imageInputMode === 'upload' ? 'bg-brand-gold text-brand-brown' : 'border-2'}`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Files
                    </Button>
                    <Button
                      type="button"
                      variant={imageInputMode === 'url' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setImageInputMode('url')}
                      className={`rounded-none ${imageInputMode === 'url' ? 'bg-brand-gold text-brand-brown' : 'border-2'}`}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      Add URL
                    </Button>
                  </div>

                  {imageInputMode === 'upload' ? (
                    <div className="space-y-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        id="image-upload"
                        data-testid="image-file-input"
                      />
                      <label
                        htmlFor="image-upload"
                        className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed cursor-pointer transition-colors ${
                          uploadingImages 
                            ? 'border-brand-gold bg-brand-gold/5' 
                            : 'border-border hover:border-brand-gold hover:bg-secondary/50'
                        }`}
                      >
                        {uploadingImages ? (
                          <>
                            <Loader2 className="w-8 h-8 text-brand-gold animate-spin mb-2" />
                            <span className="font-body text-sm text-muted-foreground">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                            <span className="font-body text-sm text-muted-foreground">
                              Click to upload or drag and drop
                            </span>
                            <span className="font-body text-xs text-muted-foreground mt-1">
                              PNG, JPG, WebP or GIF (max 10MB)
                            </span>
                          </>
                        )}
                      </label>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter image URL..."
                        value={newImageUrl}
                        onChange={(e) => setNewImageUrl(e.target.value)}
                        className="rounded-none border-2"
                        data-testid="listing-image-url-input"
                      />
                      <Button type="button" onClick={addImage} variant="outline" className="rounded-none border-2" data-testid="add-image-btn">
                        <ImagePlus className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  {/* Image Preview Grid - drag handle to reorder */}
                  {formData.images.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="font-body text-xs text-muted-foreground">
                        Drag images by the grip handle to reorder. The first image is the cover.
                      </p>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleImageDragEnd}
                      >
                        <SortableContext items={formData.images} strategy={rectSortingStrategy}>
                          <div className="grid grid-cols-4 gap-2">
                            {formData.images.map((url, idx) => (
                              <SortableImage
                                key={url}
                                id={url}
                                url={url}
                                idx={idx}
                                onRemove={removeImage}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                </div>

                {/* Custom Fields */}
                {(() => {
                  // Structured fields are edited above; don't render duplicate custom fields
                  const reservedKeys = new Set(['bedrooms', 'bathrooms', 'property_type', 'sqft', 'size']);
                  const displayableFields = customFields.filter(f => !reservedKeys.has(f.field_key));
                  if (displayableFields.length === 0) return null;
                  return (
                  <div className="md:col-span-2 space-y-4 pt-4 border-t">
                    <p className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                      Custom Fields
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {displayableFields.map((field) => (
                        <div key={field.id} className="space-y-2">
                          <Label className="font-body text-sm">
                            {field.field_name} {field.required && '*'}
                          </Label>
                          {field.field_type === 'select' && field.options ? (
                            <Select 
                              value={formData.custom_fields[field.field_key] || ''} 
                              onValueChange={(value) => updateCustomField(field.field_key, value)}
                            >
                              <SelectTrigger className="rounded-none border-2">
                                <SelectValue placeholder={`Select ${field.field_name}...`} />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options.map((opt) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : field.field_type === 'checkbox' ? (
                            <input
                              type="checkbox"
                              checked={formData.custom_fields[field.field_key] || false}
                              onChange={(e) => updateCustomField(field.field_key, e.target.checked)}
                              className="w-5 h-5"
                            />
                          ) : (
                            <Input
                              type={field.field_type === 'number' ? 'number' : 'text'}
                              value={formData.custom_fields[field.field_key] || ''}
                              onChange={(e) => updateCustomField(field.field_key, e.target.value)}
                              className="rounded-none border-2"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })()}
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit" className="rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-primary/90" data-testid="save-listing-btn">
                  {editingListing ? 'Update Listing' : 'Create Listing'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none border-2" data-testid="cancel-listing-btn">
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search listings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-none border-2 pl-10"
            data-testid="search-listings"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] rounded-none border-2" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
          </SelectContent>
        </Select>
        <Select value={propertyTypeFilter} onValueChange={setPropertyTypeFilter}>
          <SelectTrigger className="w-[180px] rounded-none border-2" data-testid="filter-property-type">
            <SelectValue placeholder="Property Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {PROPERTY_TYPES.map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-heading text-xs uppercase tracking-widest">Property</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Type</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Details</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Price</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest">Assigned To</TableHead>
              <TableHead className="font-heading text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : filteredListings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <Home className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                  <p className="font-body text-muted-foreground">No listings found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredListings.map((listing) => (
                <TableRow key={listing.id} className="hover:bg-secondary/30" data-testid={`listing-row-${listing.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {listing.images && listing.images.length > 0 ? (
                        <img src={fixImageUrl(listing.images[0])} alt={listing.title} className="w-12 h-12 object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-muted flex items-center justify-center">
                          <Home className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <p className="font-heading font-semibold text-sm">{listing.title}</p>
                        <p className="font-body text-xs text-muted-foreground">{listing.city || listing.address}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-body text-sm" data-testid={`listing-type-${listing.id}`}>
                    {listing.property_type || listing.custom_fields?.property_type || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      {listing.bedrooms && (
                        <span className="flex items-center gap-1">
                          <Bed className="w-3 h-3" />
                          {listing.bedrooms}
                        </span>
                      )}
                      {listing.bathrooms && (
                        <span className="flex items-center gap-1">
                          <Bath className="w-3 h-3" />
                          {listing.bathrooms}
                        </span>
                      )}
                      {listing.sqft && (
                        <span className="flex items-center gap-1">
                          <Square className="w-3 h-3" />
                          {listing.sqft.toLocaleString()} m²
                        </span>
                      )}
                      {!listing.bedrooms && !listing.bathrooms && !listing.sqft && (
                        <span className="text-muted-foreground/50">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-heading font-bold text-brand-gold">
                    {formatPrice(listing.price)}
                  </TableCell>
                  <TableCell>
                    <Badge className={`rounded-none font-heading text-[10px] uppercase tracking-widest ${
                      listing.status === 'sold' 
                        ? 'bg-brand-gold text-brand-brown' 
                        : listing.status === 'pending'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-brand-brown text-white'
                    }`}>
                      {listing.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    {listing.assigned_to_name || <span className="text-muted-foreground">Unassigned</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(listing)} className="p-2" data-testid={`edit-listing-${listing.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(listing.id)} className="p-2 text-destructive hover:text-destructive" data-testid={`delete-listing-${listing.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminListings;
